/**
 * mergeTabGeometry — the Founders shell's signature active-tab merge
 * geometry as one pure law (FD2; register FD-CAN-02).
 *
 * The design of record is the handoff bundle
 * `Projects/design_handoff_cockpit_nested_sessions/` — every constant here
 * is that file's Component-class math, ported verbatim: 1px strokes on the
 * half-pixel grid, top corners r=10 with reverse curl-outs r=8 (tuned; do
 * not use 12/10), main strip baseline y 34.5 with tabs 140 wide on a 148
 * pitch from x 34, nested baseline y 30.5 with tabs 132 wide on a 140
 * pitch from x 30.
 *
 * Laws this module owns:
 * - `tabX` positions a tab by its index among VISIBLE tabs — callers
 *   recompute visible order on close/fold so the bump follows reflow.
 * - The degrade predicate: when the active tab's bump cannot fit the frame
 *   width (or the active tab is not visible), the outline is a plain
 *   rounded frame and the bump is the empty path — never a floating or
 *   self-crossing bump. The caller then renders the active tab as its pill.
 *
 * Deliberately NOT here: colors, tints, and stroke alphas (FD1's contract
 * vocabulary owns every hue) and layout measurement (frame widths and
 * column heights are the caller's measured inputs). This module is pure
 * math — no React, no DOM, no store.
 */

export interface MergeTabMetrics {
  /** y of the strip's bottom edge on the half-pixel grid. */
  readonly stripBaseline: number;
  /** y of the active tab's top edge. */
  readonly tabTop: number;
  /** Top-corner radius of the active tab. */
  readonly cornerRadius: number;
  /** Reverse curl-out radius where the tab meets the baseline. */
  readonly curlRadius: number;
  /** x of the first tab. */
  readonly firstTabX: number;
  /** Tab width. */
  readonly tabWidth: number;
  /** Distance between successive tab origins (width + gap). */
  readonly tabPitch: number;
  /** Corner radius of the content frame the tab merges into. */
  readonly frameCornerRadius: number;
  /** Clearance beyond the tab's right curl required for the bump to fit. */
  readonly fitPadding: number;
}

/** Main strip metrics — strip height 34, tabs 140×34 on a 148 pitch. */
export const MAIN_TAB_METRICS: MergeTabMetrics = {
  stripBaseline: 34.5,
  tabTop: 4.5,
  cornerRadius: 10,
  curlRadius: 8,
  firstTabX: 34,
  tabWidth: 140,
  tabPitch: 148,
  frameCornerRadius: 19.5,
  fitPadding: 36,
};

/** Nested strip metrics — strip height 30, tabs 132×30 on a 140 pitch. */
export const NESTED_TAB_METRICS: MergeTabMetrics = {
  stripBaseline: 30.5,
  tabTop: 2.5,
  cornerRadius: 10,
  curlRadius: 8,
  firstTabX: 30,
  tabWidth: 132,
  tabPitch: 140,
  frameCornerRadius: 16,
  fitPadding: 36,
};

/** The empty path — every degrade branch renders this, never a partial arc. */
export const EMPTY_PATH = "M0 0";

export const PANEL_CARD_RADIUS = 16;

/** x of the tab at `visibleIndex` (index among VISIBLE tabs after reflow). */
export function tabX(visibleIndex: number, metrics: MergeTabMetrics): number {
  return metrics.firstTabX + visibleIndex * metrics.tabPitch;
}

/** Strip width the tab at `visibleIndex` needs for its bump (and itself) to fit. */
export function requiredStripWidth(visibleIndex: number, metrics: MergeTabMetrics): number {
  return tabX(visibleIndex, metrics) + metrics.tabWidth + metrics.fitPadding;
}

/** The fit predicate: false means degrade (plain frame, active tab as pill). */
export function tabFits(
  visibleIndex: number,
  frameWidth: number,
  metrics: MergeTabMetrics,
): boolean {
  return visibleIndex >= 0 && requiredStripWidth(visibleIndex, metrics) <= frameWidth;
}

/**
 * The shared ascent: curl out of the baseline, up the left side, across the
 * top, down the right side, curl back to the baseline. Starts AFTER the
 * caller's approach to (tx-8, baseline) and ends on the baseline at
 * (tx+W+8). Identical grammar at both scales.
 */
function tabSegment(tx: number, metrics: MergeTabMetrics): string {
  const { stripBaseline, tabTop, cornerRadius: r, curlRadius: c, tabWidth: W } = metrics;
  const curlTop = stripBaseline - c;
  const cornerStart = tabTop + r;
  return (
    `A${c} ${c} 0 0 0 ${tx} ${curlTop} ` +
    `L${tx} ${cornerStart} ` +
    `A${r} ${r} 0 0 1 ${tx + r} ${tabTop} ` +
    `L${tx + W - r} ${tabTop} ` +
    `A${r} ${r} 0 0 1 ${tx + W} ${cornerStart} ` +
    `L${tx + W} ${curlTop} ` +
    `A${c} ${c} 0 0 0 ${tx + W + c} ${stripBaseline}`
  );
}

export interface MainMergeInput {
  /** Measured width of the main content frame (the strip's row width). */
  frameWidth: number;
  /** Measured height of the main content column. */
  frameHeight: number;
  /** Active tab's index among VISIBLE tabs; -1 when not visible. */
  visibleIndex: number;
}

export interface MergePaths {
  /** The full frame outline (with the merged tab when it fits). */
  outline: string;
  /** The bump interior fill path (EMPTY_PATH when degraded). */
  bump: string;
  /** False = degrade: plain frame; caller renders the active tab as a pill. */
  fits: boolean;
}

/**
 * The main content frame outline with the active tab merged into its top
 * edge — the design's `mainPath`, minus its color and layout concerns.
 */
export function mainMergePath({
  frameWidth,
  frameHeight,
  visibleIndex,
}: MainMergeInput): MergePaths {
  const m = MAIN_TAB_METRICS;
  const r = m.frameCornerRadius;
  const fits = tabFits(visibleIndex, frameWidth, m);
  const tx = tabX(visibleIndex, m);
  const seg = tabSegment(tx, m);
  const top = fits ? `L${tx - m.curlRadius} ${m.stripBaseline} ${seg} ` : "";
  const outline =
    `M0.5 54 A${r} ${r} 0 0 1 20 ${m.stripBaseline} ` +
    top +
    `L${frameWidth - 20} ${m.stripBaseline} ` +
    `A${r} ${r} 0 0 1 ${frameWidth - 0.5} 54 ` +
    `L${frameWidth - 0.5} ${frameHeight - 20} ` +
    `A${r} ${r} 0 0 1 ${frameWidth - 20} ${frameHeight - 0.5} ` +
    `L20 ${frameHeight - 0.5} ` +
    `A${r} ${r} 0 0 1 0.5 ${frameHeight - 20} Z`;
  const bump = fits ? `M${tx - m.curlRadius} ${m.stripBaseline} ${seg} Z` : EMPTY_PATH;
  return { outline, bump, fits };
}

/**
 * A nested workbench strip's merged active tab — the design's `nestedPath`.
 * The nested outline is only the tab's own arc run (the panel frame draws
 * separately); a non-visible active tab degrades to empty paths.
 */
export function nestedMergePath(visibleIndex: number): MergePaths {
  const m = NESTED_TAB_METRICS;
  if (visibleIndex < 0) return { outline: EMPTY_PATH, bump: EMPTY_PATH, fits: false };
  const tx = tabX(visibleIndex, m);
  const seg = tabSegment(tx, m);
  const outline = `M${tx - m.curlRadius} ${m.stripBaseline} ${seg}`;
  const bump =
    `M${tx - m.curlRadius} ${m.stripBaseline + 1} ` +
    `L${tx - m.curlRadius} ${m.stripBaseline} ${seg} ` +
    `L${tx + m.tabWidth + m.curlRadius} ${m.stripBaseline + 1} Z`;
  return { outline, bump, fits: true };
}

/**
 * A door-local merged tab (FD3): the same ascent grammar in the door's own
 * coordinate space — baseline on the door's bottom edge, curls r=8 at x=8
 * and x=width-8, corners r=10, top at `top` (2.5 by default; the deck strip
 * passes 0.5 so the mark's ascent matches RailCard's carve line, dated
 * 2026-09-02) — left OPEN at the bottom so the active door reads as fused
 * into the row beneath it. Used by rows whose doors are content-sized (the
 * World row) where a strip-indexed `tabX` law cannot apply; the measured
 * door box supplies width and height.
 */
export function doorMergePath(width: number, height: number, top = 2.5): string {
  const baseline = height - 0.5;
  const curlTop = height - 8.5;
  const cornerTop = top + 10;
  return (
    `M0 ${baseline} A8 8 0 0 0 8 ${curlTop} ` +
    `L8 ${cornerTop} A10 10 0 0 1 18 ${top} ` +
    `L${width - 18} ${top} A10 10 0 0 1 ${width - 8} ${cornerTop} ` +
    `L${width - 8} ${curlTop} A8 8 0 0 0 ${width} ${baseline}`
  );
}

export interface SideNotchInput {
  /** y where the notch begins on the panel's outer edge. */
  notchTop: number;
  /** Full height of the framed panel. */
  panelHeight: number;
}

/**
 * A docked side panel's frame with the vertical notch bump on its right
 * edge — the design's `panelPath`: the same tab vocabulary rotated to the
 * vertical, used by the dock rail and docked context panels.
 */
export function panelSidePath({ notchTop: t, panelHeight: ph }: SideNotchInput): string {
  return (
    `M16.5 0.5 L283.5 0.5 A16 16 0 0 1 299.5 16.5 ` +
    `L299.5 ${t} A10 10 0 0 0 309.5 ${t + 10} ` +
    `L339.5 ${t + 10} A12 12 0 0 1 351.5 ${t + 22} ` +
    `L351.5 ${t + 38} A12 12 0 0 1 339.5 ${t + 50} ` +
    `L309.5 ${t + 50} A10 10 0 0 0 299.5 ${t + 60} ` +
    `L299.5 ${ph - 16.5} A16 16 0 0 1 283.5 ${ph - 0.5} ` +
    `L16.5 ${ph - 0.5} A16 16 0 0 1 0.5 ${ph - 16.5} ` +
    `L0.5 16.5 A16 16 0 0 1 16.5 0.5 Z`
  );
}

export interface SideNotchBumpInput {
  /** y where the bump's out-curl leaves the frame line. */
  notchTop: number;
  /** Height of the chip zone the bump houses (between the two corners). */
  chipHeight: number;
  /** How far the bump reaches from the frame line, on the half-pixel grid. */
  depth: number;
  /**
   * Out-curl radius where the bump leaves and rejoins the frame line. The
   * design's 36px chip curls at r=10 — the default, which keeps the
   * verbatim-port equality below. FD-CAN-27's dated rhythm refinement
   * (2026-09-01) curls the 28px door chip at r=8, the same tuning the
   * strip tabs took (12/10 down to 10/8) when their height shrank.
   */
  curl?: number;
}

/** The notch's corner radius — concentric with the r=10 chip at a 2px gap. */
const SIDE_NOTCH_CORNER = 12;

/** A chip zone too small for both corners, or a depth too shallow for the grammar. */
function sideNotchFits(chipHeight: number, depth: number, curl: number): boolean {
  return chipHeight >= SIDE_NOTCH_CORNER * 2 && depth >= curl + SIDE_NOTCH_CORNER;
}

/**
 * The run after `M<x0> t`: out-curl, across, corner, down, corner, back,
 * in-curl. `x0` is the frame line's x (0.5 for a bump in frame-line
 * coordinates; a card's right edge for panelCardPath).
 */
function sideNotchSegment(
  t: number,
  chipHeight: number,
  depth: number,
  curl: number,
  x0 = 0.5,
): string {
  const corner = SIDE_NOTCH_CORNER;
  const far = x0 + depth;
  const zoneTop = t + curl;
  const zoneBottom = zoneTop + chipHeight;
  return (
    `A${curl} ${curl} 0 0 0 ${x0 + curl} ${zoneTop} ` +
    `L${far - corner} ${zoneTop} A${corner} ${corner} 0 0 1 ${far} ${zoneTop + corner} ` +
    `L${far} ${zoneBottom - corner} A${corner} ${corner} 0 0 1 ${far - corner} ${zoneBottom} ` +
    `L${x0 + curl} ${zoneBottom} A${curl} ${curl} 0 0 0 ${x0} ${zoneBottom + curl}`
  );
}

/**
 * The side-notch bump alone, in frame-line coordinates — x 0.5 IS the
 * frame/border line, +x into the dock (FD27; register FD-CAN-27's dated
 * refinement). This is `panelSidePath`'s notch grammar (out-curls r=10,
 * corners r=12) parameterized for measured chips: the design's fixed
 * t+10/t+50/x-351.5 constants generalize to chipHeight/depth, and with
 * the design's own inputs (chipHeight 40, depth 52) the run reproduces
 * the verbatim port exactly — the story proves that equality. A chip
 * zone too small for both corners, or a depth too shallow for the
 * grammar, degrades to the empty path (never a partial arc).
 */
export function sideNotchBumpPath({
  notchTop: t,
  chipHeight,
  depth,
  curl = 10,
}: SideNotchBumpInput): string {
  if (!sideNotchFits(chipHeight, depth, curl)) return EMPTY_PATH;
  return `M0.5 ${t} ${sideNotchSegment(t, chipHeight, depth, curl)}`;
}

export type PanelEdge = "left" | "right" | "top";

/**
 * A carve on one edge of a ringed card, in the card's own coordinates
 * (FD-CAN-28/29/30). `at` is where the straight zone begins along the edge
 * (y for a side, x for the top), `size` its length, `depth` how far the
 * bump reaches from the frame line (the edge's own half-pixel column or
 * row), `curl` the out-curl radius and `corner` the zone's corner radius:
 * sides carve door chips (curl 8, corner 12 at the 28px chip; the design's
 * 36px chip at 10/12), the top carves a strip tab (curl 8, corner 10 — the
 * main strip's own tuning).
 */
export interface PanelNotch {
  readonly edge: PanelEdge;
  readonly at: number;
  readonly size: number;
  readonly depth: number;
  readonly curl: number;
  readonly corner: number;
  /**
   * DATED 2026-09-07 (the principal's crop, "not enough space between the
   * curves"): a TOP carve near a card corner no longer drops to the tab's
   * own chrome — the card corner beside it shrinks toward the curl, so the
   * corner arc and the out-curl read as one continuous wave. The card's
   * corner radius on the carve's start side (top-left) and end side
   * (top-right); absent, the card's own radius — every earlier path is
   * reproduced byte for byte.
   */
  readonly cornerStart?: number;
  readonly cornerEnd?: number;
}

export interface PanelCardInput {
  /** Measured width of the card, on the whole-pixel grid. */
  width: number;
  /** Measured height of the card. */
  height: number;
  /** The card's corner radius (the design's 16). */
  radius: number;
  /** The carve, or null for the plain rounded ring (wide dock, no door, door out of view). */
  notch: PanelNotch | null;
  /**
   * A SECOND carve on the top edge beside a side notch (FD-CAN-32 refined
   * 2026-09-02): the Cleo card carves its addressed session from its left
   * edge AND its session tab from its top. Absent or unfit, the path is the
   * one-notch path byte for byte.
   */
  topNotch?: PanelNotch | null;
  /** Where the card's top-left corner sits in the drawing's coordinates (a layer's padding). */
  origin?: { readonly x: number; readonly y: number };
}

/**
 * Transform every coordinate of a path run (M/L/A grammar only, the
 * vocabulary this module emits). `flipSweep` reverses arc orientation —
 * required when the transform is a reflection.
 */
function transformPath(
  d: string,
  map: (x: number, y: number) => readonly [number, number],
  flipSweep: boolean,
): string {
  const num = (value: number): string => String(Math.round(value * 1000) / 1000);
  return d.replace(
    /([ML])(-?[\d.]+) (-?[\d.]+)|(A)(-?[\d.]+) (-?[\d.]+) (-?[\d.]+) ([01]) ([01]) (-?[\d.]+) (-?[\d.]+)/g,
    (match, cmd, x, y, arc, rx, ry, rot, large, sweep, ax, ay) => {
      if (cmd !== undefined) {
        const [nx, ny] = map(Number(x), Number(y));
        return `${cmd}${num(nx)} ${num(ny)}`;
      }
      if (arc !== undefined) {
        const [nx, ny] = map(Number(ax), Number(ay));
        const flag = flipSweep ? (sweep === "1" ? "0" : "1") : sweep;
        return `A${rx} ${ry} ${rot} ${large} ${flag} ${num(nx)} ${num(ny)}`;
      }
      return match;
    },
  );
}

/** Reflect a card path about the card's vertical center line — the left carve IS the right carve mirrored. */
export function mirrorPathX(d: string, width: number): string {
  return transformPath(d, (x, y) => [width - x, y], true);
}

/** Translate a card path by (dx, dy) — a layer's padding around the card. */
export function shiftPath(d: string, dx: number, dy: number): string {
  if (dx === 0 && dy === 0) return d;
  return transformPath(d, (x, y) => [x + dx, y + dy], false);
}

/**
 * The top-edge carve after `L(at − curl) 0.5`: out-curl UP off the card's
 * top line, up the tab's side, across its top, down, in-curl back — the
 * design's `tabSegment` grammar for a measured tab. With the main strip's
 * inputs (size 140, depth 30, curl 8, corner 10, origin y 34) it
 * reproduces `mainMergePath`'s tab run byte for byte.
 */
function topNotchSegment(
  at: number,
  size: number,
  depth: number,
  curl: number,
  corner: number,
): string {
  const far = 0.5 - depth;
  const x1 = at + size;
  return (
    `A${curl} ${curl} 0 0 0 ${at} ${0.5 - curl} ` +
    `L${at} ${far + corner} A${corner} ${corner} 0 0 1 ${at + corner} ${far} ` +
    `L${x1 - corner} ${far} A${corner} ${corner} 0 0 1 ${x1} ${far + corner} ` +
    `L${x1} ${0.5 - curl} A${curl} ${curl} 0 0 0 ${x1 + curl} 0.5`
  );
}

function notchFits(notch: PanelNotch): boolean {
  return notch.size >= notch.corner * 2 && notch.depth >= notch.curl + notch.corner;
}

/** The right-edge side segment with an explicit corner radius (sideNotchSegment fixes the design's 12). */
function rightNotchSegment(
  t: number,
  size: number,
  depth: number,
  curl: number,
  corner: number,
  x0: number,
): string {
  const far = x0 + depth;
  const zoneTop = t + curl;
  const zoneBottom = zoneTop + size;
  return (
    `A${curl} ${curl} 0 0 0 ${x0 + curl} ${zoneTop} ` +
    `L${far - corner} ${zoneTop} A${corner} ${corner} 0 0 1 ${far} ${zoneTop + corner} ` +
    `L${far} ${zoneBottom - corner} A${corner} ${corner} 0 0 1 ${far - corner} ${zoneBottom} ` +
    `L${x0 + curl} ${zoneBottom} A${curl} ${curl} 0 0 0 ${x0} ${zoneBottom + curl}`
  );
}

/**
 * The LEFT-edge segment traversed UPWARD (the closing run of a card that also
 * carves its top): the right segment mirrored and reversed — both flips
 * cancel, so every sweep flag matches the right form's.
 */
function leftNotchSegmentUp(
  t: number,
  size: number,
  depth: number,
  curl: number,
  corner: number,
  x0: number,
): string {
  const far = x0 - depth;
  const zoneTop = t + curl;
  const zoneBottom = zoneTop + size;
  return (
    `A${curl} ${curl} 0 0 0 ${x0 - curl} ${zoneBottom} ` +
    `L${far + corner} ${zoneBottom} A${corner} ${corner} 0 0 1 ${far} ${zoneBottom - corner} ` +
    `L${far} ${zoneTop + corner} A${corner} ${corner} 0 0 1 ${far + corner} ${zoneTop} ` +
    `L${x0 - curl} ${zoneTop} A${curl} ${curl} 0 0 0 ${x0} ${zoneTop - curl}`
  );
}

/**
 * The top carve's interior as a closed region (the bump), for the fade that
 * lets a tab dissolve into its card (FD-CAN-32 refined 2026-09-02: "a fade
 * top to bottom where bottom is 100% transparent"). Empty when unfit.
 */
export function topNotchBumpPath(
  notch: PanelNotch | null,
  origin?: { readonly x: number; readonly y: number },
): string {
  if (notch === null || notch.edge !== "top" || !notchFits(notch)) return EMPTY_PATH;
  const d = `M${notch.at - notch.curl} 0.5 ${topNotchSegment(notch.at, notch.size, notch.depth, notch.curl, notch.corner)} Z`;
  return origin === undefined ? d : shiftPath(d, origin.x, origin.y);
}

/**
 * The active door's TRACE — the open silhouette of the carve with a tail
 * along the card's edge beyond each return curl. The caller keeps the
 * silhouette's stroke solid and fades only the tails, so the active door
 * reads as a heavier line that dissolves back into the card's 1px ring.
 * `start` is the tail before the carve in reading order (to the LEFT of a
 * top carve, ABOVE a side carve), `end` the tail after it (right / below).
 */
export interface TraceTails {
  readonly start: number;
  readonly end: number;
}

/**
 * ONE tail law for every edge (the top strip's measure, dated 2026-09-02;
 * carried to the side carves 2026-09-05): prefer 84% of the carve's size
 * clamped to 80–192, then cut to the straight room before the card's
 * corners — left and right of a top carve (`width`), above and below a side
 * carve (`height`). A dock door's size is its 28px chip in BOTH dock
 * densities, so its tails measure the same in the tight and the wide dock;
 * only the carve's depth changes with the mode, and the stroke simply
 * follows it. Never negative.
 */
export function panelNotchTraceTails(notch: PanelNotch, width: number, height: number): TraceTails {
  const preferred = Math.max(80, Math.min(192, notch.size * 0.84));
  const clamp = (room: number) => Math.max(0, Math.min(preferred, room));
  const inset = PANEL_CARD_RADIUS + 0.5;
  if (notch.edge === "top") {
    // The blended corners (2026-09-07) free the room a shrunken corner gave up.
    const startInset = (notch.cornerStart ?? PANEL_CARD_RADIUS) + 0.5;
    const endInset = (notch.cornerEnd ?? PANEL_CARD_RADIUS) + 0.5;
    return {
      start: clamp(notch.at - notch.curl - startInset),
      end: clamp(width - endInset - (notch.at + notch.size + notch.curl)),
    };
  }
  return {
    start: clamp(notch.at - inset),
    end: clamp(height - inset - (notch.at + notch.curl * 2 + notch.size)),
  };
}

/**
 * The trace's axis in the drawing's coordinates — from the start tail's tip
 * to the end tail's tip along the carved edge: the line a fade gradient runs
 * on (horizontal on the top edge, vertical on a side).
 */
export function panelNotchTraceAxis(
  notch: PanelNotch,
  width: number,
  origin: { readonly x: number; readonly y: number },
  tails: TraceTails,
): { readonly x1: number; readonly y1: number; readonly x2: number; readonly y2: number } {
  if (notch.edge === "top") {
    const y = origin.y + 0.5;
    return {
      x1: origin.x + notch.at - notch.curl - tails.start,
      y1: y,
      x2: origin.x + notch.at + notch.size + notch.curl + tails.end,
      y2: y,
    };
  }
  const x = origin.x + (notch.edge === "right" ? width - 0.5 : 0.5);
  return {
    x1: x,
    y1: origin.y + notch.at - tails.start,
    x2: x,
    y2: origin.y + notch.at + notch.curl * 2 + notch.size + tails.end,
  };
}

/**
 * The trace path on any edge: the start tail along the frame line, the
 * carve's own run (`topNotchSegment` on the top; `rightNotchSegment` on the
 * right, and the LEFT is that form mirrored — the module's own law), then
 * the end tail. Empty when the carve cannot fit (never a partial arc).
 */
export function panelNotchTracePath(
  notch: PanelNotch | null,
  width: number,
  origin: { readonly x: number; readonly y: number },
  tails: TraceTails,
): string {
  if (notch === null || !notchFits(notch)) return EMPTY_PATH;
  const { at, size, depth, curl, corner } = notch;
  let d: string;
  if (notch.edge === "top") {
    const start = at - curl;
    const end = at + size + curl;
    d =
      `M${start - tails.start} 0.5 L${start} 0.5 ` +
      `${topNotchSegment(at, size, depth, curl, corner)} ` +
      `L${end + tails.end} 0.5`;
  } else {
    const right = width - 0.5;
    const end = at + curl * 2 + size;
    const rightForm =
      `M${right} ${at - tails.start} L${right} ${at} ` +
      `${rightNotchSegment(at, size, depth, curl, corner, right)} ` +
      `L${right} ${end + tails.end}`;
    d = notch.edge === "right" ? rightForm : mirrorPathX(rightForm, width);
  }
  return shiftPath(d, origin.x, origin.y);
}

/** The same closed carve region on any edge; used by the door fade. */
export function panelNotchBumpPath(
  notch: PanelNotch | null,
  width: number,
  origin: { readonly x: number; readonly y: number },
): string {
  if (notch === null || !notchFits(notch)) return EMPTY_PATH;
  if (notch.edge === "top") return topNotchBumpPath(notch, origin);
  const { at, size, depth, curl, corner } = notch;
  const d =
    notch.edge === "right"
      ? `M${width - 0.5} ${at} ${rightNotchSegment(at, size, depth, curl, corner, width - 0.5)} Z`
      : `M0.5 ${at + size + 2 * curl} ${leftNotchSegmentUp(at, size, depth, curl, corner, 0.5)} Z`;
  return shiftPath(d, origin.x, origin.y);
}

/**
 * A ringed panel card's outline as ONE closed path — the design of record's
 * `panelPath` generalized (FD-CAN-28), then given its other edges
 * (FD-CAN-29: the LEFT carve is the right carve mirrored, `mirrorPathX`;
 * FD-CAN-30: the TOP carve is the strip's tab ascent, `mainMergePath`'s
 * own run). With the design's inputs (width 300, radius 16, size 40, depth
 * 52, curl 10, corner 12) the right form reproduces `panelSidePath` byte
 * for byte — the stories prove each equality. A carve that cannot fit
 * degrades to the plain ring (never a partial arc); the ring always exists.
 */
export function panelCardPath({
  width: w,
  height: h,
  radius: r,
  notch,
  topNotch = null,
  origin,
}: PanelCardInput): string {
  const right = w - 0.5;
  const bottom = h - 0.5;
  // DATED 2026-09-07: the two top corners beside a DRAWN top carve wear the
  // carve's blended radii (PanelNotch.cornerStart / cornerEnd); absent, the
  // card's own, so every earlier path is reproduced byte for byte.
  const topCarve =
    topNotch !== null && topNotch.edge === "top" && notchFits(topNotch)
      ? topNotch
      : notch !== null && notch.edge === "top" && notchFits(notch)
        ? notch
        : null;
  const rStart = topCarve?.cornerStart ?? r;
  const rEnd = topCarve?.cornerEnd ?? r;
  const topRightCorner = `A${rEnd} ${rEnd} 0 0 1 ${right} ${0.5 + rEnd} `;
  const tail =
    `L${right} ${bottom - r} A${r} ${r} 0 0 1 ${right - r} ${bottom} ` +
    `L${0.5 + r} ${bottom} A${r} ${r} 0 0 1 0.5 ${bottom - r} ` +
    `L0.5 ${0.5 + rStart} A${rStart} ${rStart} 0 0 1 ${0.5 + rStart} 0.5 Z`;
  const plain = `M${0.5 + r} 0.5 L${right - r} 0.5 ${topRightCorner}${tail}`;
  let d: string;
  if (topNotch !== null && topNotch.edge === "top" && notchFits(topNotch)) {
    // Two edges on one card: the top run carries the tab, then the side
    // notch (if any) is drawn in place — the left one upward, directly.
    const topRun =
      `M${0.5 + rStart} 0.5 L${topNotch.at - topNotch.curl} 0.5 ` +
      `${topNotchSegment(topNotch.at, topNotch.size, topNotch.depth, topNotch.curl, topNotch.corner)} ` +
      `L${right - rEnd} 0.5 ${topRightCorner}`;
    if (notch === null || !notchFits(notch) || notch.edge === "top") {
      d = topRun + tail;
    } else if (notch.edge === "right") {
      d =
        topRun +
        `L${right} ${notch.at} ${rightNotchSegment(notch.at, notch.size, notch.depth, notch.curl, notch.corner, right)} ` +
        tail;
    } else {
      const zoneEnd = notch.at + notch.curl * 2 + notch.size;
      d =
        topRun +
        `L${right} ${bottom - r} A${r} ${r} 0 0 1 ${right - r} ${bottom} ` +
        `L${0.5 + r} ${bottom} A${r} ${r} 0 0 1 0.5 ${bottom - r} ` +
        `L0.5 ${zoneEnd} ${leftNotchSegmentUp(notch.at, notch.size, notch.depth, notch.curl, notch.corner, 0.5)} ` +
        `L0.5 ${0.5 + rStart} A${rStart} ${rStart} 0 0 1 ${0.5 + rStart} 0.5 Z`;
    }
  } else if (notch === null || !notchFits(notch)) {
    d = plain;
  } else if (notch.edge === "top") {
    d =
      `M${0.5 + rStart} 0.5 L${notch.at - notch.curl} 0.5 ` +
      `${topNotchSegment(notch.at, notch.size, notch.depth, notch.curl, notch.corner)} ` +
      `L${right - rEnd} 0.5 ${topRightCorner}${tail}`;
  } else {
    const rightForm =
      `M${0.5 + r} 0.5 L${right - r} 0.5 ${topRightCorner}` +
      `L${right} ${notch.at} ` +
      `${rightNotchSegment(notch.at, notch.size, notch.depth, notch.curl, notch.corner, right)} ` +
      tail;
    d = notch.edge === "right" ? rightForm : mirrorPathX(rightForm, w);
  }
  return origin === undefined ? d : shiftPath(d, origin.x, origin.y);
}

export interface CleoRailInput {
  /** Height of the Cleo rail's column (the shell's measured column height). */
  columnHeight: number;
  /**
   * y where the dock's side notch meets the rail's left edge, or null for
   * the plain closed outline (the notch's own degrade — e.g. wide dock).
   */
  notchTop: number | null;
}

/**
 * The Cleo rail's framed outline with its own strip tab merged at the top
 * and an optional left-edge notch toward the dock — the design's
 * `cleoPath`.
 */
export function cleoRailPath({ columnHeight: ch, notchTop: t }: CleoRailInput): {
  outline: string;
  bump: string;
} {
  const base =
    `M54.5 50.5 A16 16 0 0 1 70.5 34.5 L74.5 34.5 ` +
    `A8 8 0 0 0 82.5 26.5 L82.5 14.5 A10 10 0 0 1 92.5 4.5 ` +
    `L180.5 4.5 A10 10 0 0 1 190.5 14.5 L190.5 26.5 ` +
    `A8 8 0 0 0 198.5 34.5 L347.5 34.5 A18 18 0 0 1 365.5 52.5 ` +
    `L365.5 ${ch - 18.5} A18 18 0 0 1 347.5 ${ch - 0.5} ` +
    `L72.5 ${ch - 0.5} A18 18 0 0 1 54.5 ${ch - 18.5} `;
  if (t == null) return { outline: `${base}L54.5 50.5 Z`, bump: EMPTY_PATH };
  const seg =
    `A8 8 0 0 0 46.5 ${t + 40} L12.5 ${t + 40} ` +
    `A10 10 0 0 1 2.5 ${t + 30} L2.5 ${t + 10} ` +
    `A10 10 0 0 1 12.5 ${t} L46.5 ${t} A8 8 0 0 0 54.5 ${t - 8}`;
  return {
    outline: `${base}L54.5 ${t + 48} ${seg} L54.5 50.5 Z`,
    bump: `M54.5 ${t + 48} ${seg} Z`,
  };
}

export interface UniformTabWidthInput {
  /** Width available to the tabs alone — the row's viewport less any fixed neighbour seated in the track. */
  readonly available: number;
  /** How many tabs share the row. */
  readonly count: number;
  /** The gap between successive tabs. */
  readonly gap: number;
  /** The floor — a tab never narrows below it; the row scrolls instead. */
  readonly min: number;
  /** The ceiling — a tab never widens past it, however few share the row. */
  readonly max: number;
}

/**
 * FD-CAN-34 (2026-09-02, the principal's word over the design's fixed 140):
 * every tab in a strip row is ONE width — as wide as the ceiling allows,
 * shared equally when the row tightens, never below the floor. Below the
 * floor the row scrolls; nothing here folds or hides a tab (the design's
 * force-fold is a named absence). Pure: the caller measures the row and
 * supplies the count. Whole pixels, floored, so a row of dealt tabs never
 * overflows by the rounding.
 */
export function uniformTabWidth({ available, count, gap, min, max }: UniformTabWidthInput): number {
  if (count <= 0) return max;
  const share = (available - Math.max(0, count - 1) * gap) / count;
  return Math.floor(Math.min(max, Math.max(min, share)));
}

/** Give the selected label room first, then converge to equal widths as the rail grows. */
export function prioritizedTabWidths(input: UniformTabWidthInput & { preferred: number }): {
  active: number;
  inactive: number;
} {
  const equal = uniformTabWidth(input);
  if (input.count <= 1) return { active: equal, inactive: equal };
  const space = input.available - (input.count - 1) * input.gap;
  const active = Math.floor(
    Math.max(equal, Math.min(input.max, input.preferred, space - (input.count - 1) * input.min)),
  );
  const inactive = Math.floor(
    Math.min(input.max, Math.max(input.min, (space - active) / (input.count - 1))),
  );
  return { active, inactive };
}

/** True when the row must scroll: every tab at the floor still overflows `available`. */
export function uniformTabsOverflow({ available, count, gap, min }: UniformTabWidthInput): boolean {
  return count * min + Math.max(0, count - 1) * gap > available;
}

/**
 * The floors and ceilings at both scales — the FALLBACK for the CSS seats
 * (`--deck-tab-min` / `--deck-tab-max` / `--deck-tab-gap` in index.css, which
 * a nested primitive level re-declares). The component reads the seats; a
 * static render and a story read these.
 */
export const DECK_TAB_LAYOUT = {
  // DATED 2026-09-07: the gap is the carve's reach (curl 8 + notch gap 2) plus 2 of air.
  main: { min: 104, max: 184, gap: 12 },
  nested: { min: 96, max: 168, gap: 12 },
} as const;
